/**
 * P2.2: Cola de mutaciones offline (IndexedDB).
 * API: enqueueMutation, getPendingMutations, removeMutation, clearQueue.
 */
const DB_NAME = 'partediario_offline';
const STORE_NAME = 'sync_queue';
const DB_VERSION = 1;

export type MutationType =
    | 'createOrder'
    | 'updateOrder'
    | 'deleteOrder'
    | 'deleteOrders'
    | 'deleteEvent'
    | 'updateEvent';

export interface QueuedMutation {
    id: string;
    type: MutationType;
    payload: Record<string, unknown>;
    createdAt: number;
    retries?: number;
    lastError?: string;
}

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        try {
            if (!window.indexedDB) {
                return reject(new Error('IndexedDB no está disponible en este entorno.'));
            }
            const req = window.indexedDB.open(DB_NAME, DB_VERSION);
            req.onerror = () => reject(req.error || new Error('Error al abrir IndexedDB'));
            req.onsuccess = () => resolve(req.result);
            req.onupgradeneeded = (e) => {
                const db = (e.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            };
        } catch (error) {
            reject(error);
        }
    });
}

// Fallback memory queue para modo estricto
let memoryQueue: QueuedMutation[] = [];
let useMemoryFallback = false;

function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => void): Promise<T> {
    if (useMemoryFallback) {
        return Promise.resolve() as unknown as Promise<T>; // En modo memory, interceptamos antes
    }
    return openDB().then((db) => {
        return new Promise<T>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, mode);
            const store = tx.objectStore(STORE_NAME);
            fn(store);
            tx.oncomplete = () => resolve(undefined as T);
            tx.onerror = () => reject(tx.error);
            db.close();
        });
    }).catch(err => {
        console.warn('IndexedDB falló. Activando fallback en memoria.', err);
        useMemoryFallback = true;
        // La primera vez que falla, ya interceptamos a partir de ahora
        return Promise.resolve(undefined as unknown as T);
    });
}

export function enqueueMutation(type: MutationType, payload: Record<string, unknown>): Promise<string> {
    const id = `mq-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const item: QueuedMutation = { id, type, payload, createdAt: Date.now() };
    
    if (useMemoryFallback) {
        memoryQueue.push(item);
        // Intentar guardar en localStorage por si acaso
        try { localStorage.setItem('pd_memory_queue', JSON.stringify(memoryQueue)); } catch(e) {}
        return Promise.resolve(id);
    }
    
    return withStore<void>('readwrite', (store) => store.add(item)).then(() => id).catch(() => {
        // Si falla en el último momento
        memoryQueue.push(item);
        return id;
    });
}

export function getPendingMutations(): Promise<QueuedMutation[]> {
    if (useMemoryFallback) {
        // Intentar recuperar de localStorage si recargó
        if (memoryQueue.length === 0) {
            try { 
                const stored = localStorage.getItem('pd_memory_queue');
                if (stored) memoryQueue = JSON.parse(stored);
            } catch(e) {}
        }
        return Promise.resolve([...memoryQueue].sort((a, b) => a.createdAt - b.createdAt));
    }
    
    return openDB().then((db) => {
        return new Promise<QueuedMutation[]>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.getAll();
            req.onsuccess = () => {
                const rows = (req.result as QueuedMutation[]).sort((a, b) => a.createdAt - b.createdAt);
                resolve(rows);
            };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => db.close();
        });
    }).catch(() => {
        return Promise.resolve([...memoryQueue].sort((a, b) => a.createdAt - b.createdAt));
    });
}

export function removeMutation(id: string): Promise<void> {
    if (useMemoryFallback) {
        memoryQueue = memoryQueue.filter(m => m.id !== id);
        try { localStorage.setItem('pd_memory_queue', JSON.stringify(memoryQueue)); } catch(e) {}
        return Promise.resolve();
    }
    return withStore<void>('readwrite', (store) => store.delete(id)).catch(() => {
        memoryQueue = memoryQueue.filter(m => m.id !== id);
        return Promise.resolve();
    });
}

export function updateMutation(item: QueuedMutation): Promise<void> {
    if (useMemoryFallback) {
        memoryQueue = memoryQueue.map(m => m.id === item.id ? item : m);
        try { localStorage.setItem('pd_memory_queue', JSON.stringify(memoryQueue)); } catch(e) {}
        return Promise.resolve();
    }
    return withStore<void>('readwrite', (store) => store.put(item)).catch(() => {
        memoryQueue = memoryQueue.map(m => m.id === item.id ? item : m);
        return Promise.resolve();
    });
}

export function quarantineMutation(item: QueuedMutation, reason: string): Promise<void> {
    try {
        const deadLetterKey = 'pd_dead_letter_queue';
        const existing = JSON.parse(localStorage.getItem(deadLetterKey) || '[]');
        existing.push({
            ...item,
            quarantinedAt: Date.now(),
            failureReason: reason
        });
        localStorage.setItem(deadLetterKey, JSON.stringify(existing.slice(-50))); // Keep last 50
    } catch (e) {
        console.warn('[OfflineSync] Failed to store dead-letter mutation:', e);
    }
    return removeMutation(item.id);
}

export function getDeadLetterMutations(): any[] {
    try {
        const deadLetterKey = 'pd_dead_letter_queue';
        const stored = localStorage.getItem(deadLetterKey);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

export function clearDeadLetterQueue(): void {
    try {
        localStorage.removeItem('pd_dead_letter_queue');
    } catch {}
}

export function clearQueue(): Promise<void> {
    if (useMemoryFallback) {
        memoryQueue = [];
        try { localStorage.removeItem('pd_memory_queue'); } catch(e) {}
        return Promise.resolve();
    }
    return withStore<void>('readwrite', (store) => store.clear()).catch(() => {
        memoryQueue = [];
        return Promise.resolve();
    });
}
